import type { AdminUserRole } from '@shared/types';
import { ADMIN_GAME_MODULES } from './adminModules';
import type {
  AdminGameModule,
  AdminNavGroup,
  AdminNavItem,
  AdminSurfaceRoute,
} from './types';

/**
 * Lookups over the registered admin game modules (#817). Each takes the
 * module list as a defaulted parameter so the wiring is testable with fake
 * games, independent of which real games ship admin pages.
 */

/** The module of the given (active) game, if it ships admin pages. */
export function adminModuleFor(
  gameId: string,
  modules: readonly AdminGameModule[] = ADMIN_GAME_MODULES,
): AdminGameModule | undefined {
  return modules.find((m) => m.gameId === gameId);
}

/** A surface-gated route: rendered behind `RequireSurface surface=…`. */
export interface SurfaceRouteEntry extends AdminSurfaceRoute {
  surface: string;
}

/**
 * Every game surface route, across ALL registered games. Registration is
 * unconditional — the per-request gate is `RequireSurface`, driven by the
 * server's declared surfaces for the active game.
 */
export function surfaceRoutes(
  modules: readonly AdminGameModule[] = ADMIN_GAME_MODULES,
): SurfaceRouteEntry[] {
  return modules.flatMap((m) =>
    m.surfaces.flatMap((s) => s.routes.map((r) => ({ ...r, surface: s.surface }))),
  );
}

/** A game nav item tagged with the surface that gates it. */
export interface SurfaceNavItem extends AdminNavItem {
  surface: string;
}

/** Game nav items for one sidebar group, in registration + manifest order. */
export function surfaceNavItems(
  group: AdminNavGroup,
  modules: readonly AdminGameModule[] = ADMIN_GAME_MODULES,
): SurfaceNavItem[] {
  return modules.flatMap((m) =>
    m.surfaces.flatMap((s) =>
      s.navItems
        .filter((item) => item.group === group)
        .map((item) => ({ ...item, surface: s.surface })),
    ),
  );
}

/**
 * The active game's landing paths, for `adminHomePath`. A role the active
 * game names no landing for falls back to the dashboard there.
 *
 * Before the game list has loaded, no game is chosen yet (`''` — the server's
 * default answers unqualified requests, and the SPA does not hardcode which
 * game that is). A role whose only home is a game's content page (the lateral
 * `content_manager`, which the dashboard refuses) must still land somewhere
 * it can use, or the redirect would bounce it back to the dashboard forever;
 * so an unchosen game takes the first manifest's landing for each role. The
 * page's own surface gate re-checks it once the active game is known.
 */
export function landingPathsFor(
  gameId: string,
  modules: readonly AdminGameModule[] = ADMIN_GAME_MODULES,
): Partial<Record<AdminUserRole, string>> {
  if (!gameId) {
    const merged: Partial<Record<AdminUserRole, string>> = {};
    for (const m of modules) {
      for (const [role, path] of Object.entries(m.landingPaths ?? {})) {
        merged[role as AdminUserRole] ??= path;
      }
    }
    return merged;
  }
  return adminModuleFor(gameId, modules)?.landingPaths ?? {};
}
